import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './WorkstationTab.css';
import { pdf } from '@react-pdf/renderer';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { FormularioCampoPDFDocument, urlParaBase64 } from './FormularioCampoPDF';
import { supabase } from '../supabaseClient';
import { downloadXlsx } from '../utils/xlsxExport';


/* ─────────────────────────────────────────────
   WorkstationTab — Formulários de Campo
───────────────────────────────────────────── */

const WorkstationTab = ({
    registros,
    serverPage = 1,
    serverTotal = 0,
    serverFilters = {},
    allAgentsList = [],
    municipioNome = '',
    onPageChange,
    onFilterChange,
    onFetchAll,          // async () => [...todos os registros]
}) => {
    const navigate = useNavigate();

    const [modoSelecao, setModoSelecao] = useState(false);
    const [selecionados, setSelecionados] = useState(new Set());
    const [modoTodos, setModoTodos]       = useState(false); // "selecionar todos do servidor"
    const [gerando, setGerando]           = useState(false);
    const [tipoGeracao, setTipoGeracao]   = useState(null);
    const [progresso, setProgresso]       = useState({ total: 0, atual: 0 });

    const toggleModoSelecao = () => {
        setModoSelecao(p => !p);
        setSelecionados(new Set());
        setModoTodos(false);
        setTipoGeracao(null);
        setProgresso({ total: 0, atual: 0 });
    };

    const toggleSelecionado = (id) => {
        setModoTodos(false);
        setSelecionados(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleTodos = () => {
        setModoTodos(false);
        if (selecionados.size === registros.length) {
            setSelecionados(new Set());
        } else {
            setSelecionados(new Set(registros.map(r => r.id)));
        }
    };

    const todaPaginaSelecionada = registros.length > 0 && selecionados.size === registros.length;

    const sanitize = (str) =>
        String(str || '').trim()
            .normalize('NFD').replace(/[̀-ͯ]/g, '')
            .replace(/[^a-zA-Z0-9\s]/g, '')
            .replace(/\s+/g, '_')
            .slice(0, 25);

    const parseArray = (field) => {
        if (!field) return [];
        if (Array.isArray(field)) return field;
        if (typeof field === 'string') {
            const trimmed = field.trim();
            if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
                return trimmed
                    .slice(1, -1)
                    .split(',')
                    .map(s => s.trim().replace(/^"|"$/g, ''))
                    .filter(s => s !== "");
            }
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) return parsed;
            } catch (e) {
                return trimmed.split(',').map(s => s.trim()).filter(s => s !== "");
            }
        }
        return [field];
    };

    const labelMap = {
        semPendencias: 'Sem pendências',
        fechado: 'Domicílio fechado',
        recusa: 'Recusa',
        nova: 'Nova (primeira pesquisa)',
        reconhecida: 'Reconhecida (já foi pesquisada)',
        demolida: 'Demolida',
        alvenaria_reboco: 'Alvenaria c/ reboco',
        alvenaria_sem_reboco: 'Alvenaria s/ reboco',
        barro_reboco: 'Barro c/ reboco',
        barro_sem_reboco: 'Barro s/ reboco',
        madeira: 'Madeira',
        taipa: 'Taipa',
        palha: 'Palha',
        telha: 'Telha',
        metalico: 'Metálico',
        outros: 'Outros',
        Urbana: 'Urbana',
        Rural: 'Rural',
        Nenhum: 'Nenhum',
        Ovos: 'Ovos',
    };

    const mapValueToLabel = (value) => {
        if (value === true) return 'Sim';
        if (value === false) return 'Não';
        if (value === null || value === undefined || value === '') return '';
        return labelMap[value] || labelMap[String(value).trim()] || String(value);
    };

    const formatarData = (value) => {
        if (!value) return '';
        const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) return `${match[3]}/${match[2]}/${match[1]}`;
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('pt-BR');
    };

    const formatarDataHora = (value) => {
        if (!value) return '';
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('pt-BR');
    };

    const parseDataFormulario = (value) => {
        if (!value) return null;
        const texto = String(value);
        const match = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return null;
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    };

    const formatarDataArquivo = (date) => {
        const dia = String(date.getUTCDate()).padStart(2, '0');
        const mes = String(date.getUTCMonth() + 1).padStart(2, '0');
        const ano = date.getUTCFullYear();
        return `${dia}-${mes}-${ano}`;
    };

    const montarPeriodoArquivo = (lista) => {
        const datas = (lista || [])
            .map(row => parseDataFormulario(row.data_atividade || row.data || row.created_at || row.ocorrencia_data_registro))
            .filter(Boolean)
            .sort((a, b) => a.getTime() - b.getTime());

        if (datas.length === 0) return formatarDataArquivo(new Date());

        const primeira = datas[0];
        const ultima = datas[datas.length - 1];
        const primeiraTexto = formatarDataArquivo(primeira);
        const ultimaTexto = formatarDataArquivo(ultima);

        return primeiraTexto === ultimaTexto ? primeiraTexto : `${primeiraTexto}-a-${ultimaTexto}`;
    };

    const formatarAtividadePlanilha = (field) => {
        const mapa = {
            pesquisa: 'Pesquisa',
            borrifacao: 'Borrifação',
            'borrifação': 'Borrifação',
            atendimentopit: 'Atendimento ao PIT',
            pit: 'PIT',
        };
        return parseArray(field)
            .map(item => {
                const chave = String(item || '').toLowerCase().trim();
                return mapa[chave] || item;
            })
            .filter(Boolean)
            .join(' / ');
    };

    const joinUrls = (field) => parseArray(field).filter(Boolean).join('\n');

    const findAgenteNome = (row) => {
        const agenteObj = allAgentsList.find(a => a.id === row.agente_id);
        return agenteObj?.nome || agenteObj?.nome_completo || row.agente_nome || row.nome_agente_view || '';
    };

    const buscarPorIds = async (table, ids, select = '*') => {
        const uniqueIds = [...new Set((ids || []).filter(Boolean))];
        if (uniqueIds.length === 0) return [];

        const resultados = [];
        for (let i = 0; i < uniqueIds.length; i += 50) {
            const chunk = uniqueIds.slice(i, i + 50);
            const { data, error } = await supabase.from(table).select(select).in('id', chunk);
            if (error) throw error;
            resultados.push(...(data || []));
        }
        return resultados;
    };

    const montarDadosExcel = async (lista) => {
        let registrosCompletos = lista;
        try {
            const ocorrencias = await buscarPorIds('ocorrencias', lista.map(row => row.id));
            const ocorrenciasMap = Object.fromEntries(ocorrencias.map(row => [row.id, row]));
            registrosCompletos = lista.map(row => {
                const completo = ocorrenciasMap[row.id] || {};
                return {
                    ...row,
                    ...completo,
                    localidade: row.localidade || completo.nome_localidade || row.nome_localidade_view || row.nome_localidade || row.endereco || '',
                    agente_nome: row.agente_nome || row.nome_agente_view || findAgenteNome({ ...row, ...completo }),
                    tipo_atividade: parseArray(completo.tipo_atividade || row.tipo_atividade),
                    fotos_urls: parseArray(completo.fotos_urls || row.fotos_urls),
                    fotos_ficha_urls: parseArray(completo.fotos_ficha_urls || row.fotos_ficha_urls),
                };
            });
        } catch (err) {
            console.error('Erro ao buscar ocorrências completas para Excel:', err);
        }

        let denunciasMap = {};
        try {
            const denuncias = await buscarPorIds('denuncias', registrosCompletos.map(row => row.denuncia_id));
            denunciasMap = Object.fromEntries(denuncias.map(row => [row.id, row]));
        } catch (err) {
            console.error('Erro ao buscar denúncias para Excel:', err);
        }

        const headers = [
            'ID do Formulário',
            'Município',
            'Status de Sincronização',
            'Data de Registro',
            'Data da Atividade',
            'Tipo de Atividade',
            'Agente Responsável',
            'Código de Etiqueta',
            'Número do PIT',
            'Código da Localidade',
            'Nome da Localidade',
            'Categoria',
            'Endereço',
            'Número',
            'Complemento',
            'Nome do Morador',
            'Quantidade de Moradores',
            'Número Anexo',
            'Situação do Imóvel',
            'Tipo de Parede',
            'Tipo de Teto',
            'Melhoria Habitacional',
            'Pendência da Pesquisa',
            'Pendência da Borrifação',
            'Captura Intradomicílio',
            'Vestígios Intradomicílio',
            'Nº de Barbeiros Intra',
            'Captura Peridomicílio',
            'Vestígios Peridomicílio',
            'Nº de Barbeiros Peri',
            'Total de Barbeiros',
            'Inseticida',
            'Número de Cargas',
            'Latitude',
            'Longitude',
            'Qtd. Fotos Atendimento',
            'Fotos Atendimento',
            'Qtd. Fotos Ficha Física',
            'Fotos Ficha Física',
        ];

        const rows = registrosCompletos.map(row => {
            const denuncia = row.denuncia_id ? denunciasMap[row.denuncia_id] : null;
            const fotosAtendimento = parseArray(row.fotos_urls).filter(url => url && url !== denuncia?.foto_url);
            const fotosFicha = parseArray(row.fotos_ficha_urls);
            const totalIntra = Number(row.barbeiros_intradomicilio) || 0;
            const totalPeri = Number(row.barbeiros_peridomicilio) || 0;

            return [
                row.id,
                municipioNome,
                row.sincronizado ? 'Sincronizado' : 'Pendente',
                formatarDataHora(row.created_at || row.ocorrencia_data_registro),
                formatarData(row.data_atividade || row.data),
                formatarAtividadePlanilha(row.tipo_atividade),
                findAgenteNome(row),
                row.codigo_etiqueta || '',
                row.numero_pit || '',
                row.codigo_localidade || '',
                row.nome_localidade || row.nome_localidade_view || row.localidade || '',
                row.categoria_localidade || row.categoria_localidade_view || '',
                row.endereco || '',
                row.numero || '',
                row.complemento || '',
                row.nome_morador || '',
                Number(row.quantidade_moradores) || 0,
                Number(row.numero_anexo) || 0,
                mapValueToLabel(row.situacao_imovel),
                mapValueToLabel(row.tipo_parede),
                mapValueToLabel(row.tipo_teto),
                mapValueToLabel(row.melhoria_habitacional),
                mapValueToLabel(row.pendencia_pesquisa),
                mapValueToLabel(row.pendencia_borrifacao),
                totalIntra > 0 ? 'Triatomíneo' : 'Nenhum',
                mapValueToLabel(row.vestigios_intradomicilio || row.vestigios_intra),
                totalIntra,
                totalPeri > 0 ? 'Triatomíneo' : 'Nenhum',
                mapValueToLabel(row.vestigios_peridomicilio || row.vestigios_peri),
                totalPeri,
                totalIntra + totalPeri,
                row.inseticida || '',
                Number(row.numero_cargas) || 0,
                row.latitude ?? '',
                row.longitude ?? '',
                fotosAtendimento.length,
                joinUrls(fotosAtendimento),
                fotosFicha.length,
                joinUrls(fotosFicha),
            ];
        });

        return {
            headers,
            rows,
            periodoArquivo: montarPeriodoArquivo(registrosCompletos),
        };
    };

    const obterListaSelecionada = async () => {
        if (modoTodos && onFetchAll) return onFetchAll();
        return registros.filter(r => selecionados.has(r.id));
    };

    const gerarPDFsEBaixar = async (lista) => {
        if (!lista || lista.length === 0) return;
        
        setProgresso({ total: lista.length, atual: 0 });
        const zip = new JSZip();
        
        // 1. Buscar denúncias em lotes de 50 (evita URLs gigantes no fetch)
        const allDenunciaIds = [...new Set(lista.map(r => r.denuncia_id).filter(Boolean))];
        const denunciasMap = {};
        
        for (let i = 0; i < allDenunciaIds.length; i += 50) {
            const chunk = allDenunciaIds.slice(i, i + 50);
            try {
                const { data: dList } = await supabase.from('denuncias').select('*').in('id', chunk);
                if (dList) {
                    dList.forEach(d => {
                        denunciasMap[d.id] = {
                            id: d.id, rua: d.rua, numero: d.numero, bairro: d.bairro,
                            complemento: d.complemento, descricao: d.descricao,
                            createdAt: d.created_at, fotoPrincipalUrl: d.foto_url,
                            feedbackAgente: d.feedback_agente
                        };
                    });
                }
            } catch (e) {
                console.error("Erro no lote de denúncias:", e);
            }
        }

        // Cache de imagens de denúncia (evita converter a mesma foto Base64 várias vezes)
        const denunciaImgCache = {};

        // Loop principal para geração dos PDFs
        for (let i = 0; i < lista.length; i++) {
            const row = lista[i];
            setProgresso(p => ({ ...p, atual: i + 1 }));
            
            try {
                const agenteObj = allAgentsList.find(a => a.id === row.agente_id);
                const agenteNome = agenteObj?.nome || agenteObj?.nome_completo || row.agente_nome || '';
                
                const parts = [
                    sanitize(row.codigo_etiqueta),
                    sanitize(row.endereco),
                    sanitize(row.numero),
                    sanitize(row.localidade),
                ].filter(Boolean);
                const nomeArquivo = `${i+1}_FC_${parts.join('_')}.pdf`.replace(/_+/g, '_');

                const dData = row.denuncia_id ? denunciasMap[row.denuncia_id] : null;
                
                // Preparar imagens
                const rawFotos = parseArray(row.fotos_urls);
                const rawFicha = parseArray(row.fotos_ficha_urls);
                const fotosFiltradas = rawFotos.filter(u => u && u !== dData?.fotoPrincipalUrl);

                // Imagem da denúncia (com cache)
                let imgDenunciaBase64 = null;
                if (dData?.fotoPrincipalUrl) {
                    if (denunciaImgCache[dData.fotoPrincipalUrl]) {
                        imgDenunciaBase64 = denunciaImgCache[dData.fotoPrincipalUrl];
                    } else {
                        imgDenunciaBase64 = await urlParaBase64(dData.fotoPrincipalUrl);
                        denunciaImgCache[dData.fotoPrincipalUrl] = imgDenunciaBase64;
                    }
                }

                // Converter fotos do atendimento
                const [base64Fotos, base64Ficha] = await Promise.all([
                    Promise.all(fotosFiltradas.map(u => urlParaBase64(u))),
                    Promise.all(rawFicha.filter(u => u).map(u => urlParaBase64(u)))
                ]);

                const doc = (
                    <FormularioCampoPDFDocument
                        data={row}
                        agenteNome={agenteNome}
                        municipioNome={municipioNome}
                        fotosUrls={base64Fotos.filter(Boolean)}
                        fichaFotosUrls={base64Ficha.filter(Boolean)}
                        fotoDenunciaUrl={imgDenunciaBase64}
                        denunciaData={dData}
                        feedbackAgente={row.feedback_agente || dData?.feedbackAgente || ''}
                    />
                );

                const blob = await pdf(doc).toBlob();
                if (blob) {
                    zip.file(nomeArquivo, blob);
                }

                // Pausa curta para não travar o browser
                if (i % 2 === 0) await new Promise(r => setTimeout(r, 10));

            } catch (errRow) {
                console.error(`Erro no registro ${row.id}:`, errRow);
            }
        }

        try {
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            const dataHoje = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
            saveAs(zipBlob, `formularios-campo-${dataHoje}.zip`);
        } catch (errZip) {
            console.error("Erro ZIP final:", errZip);
            alert("Erro ao finalizar o arquivo ZIP.");
        } finally {
            setProgresso({ total: 0, atual: 0 });
        }
    };

    const handleBaixarZip = async () => {
        setGerando(true);
        setTipoGeracao('pdf');
        try {
            const lista = await obterListaSelecionada();

            if (!lista || lista.length === 0) {
                alert("Nenhum registro selecionado.");
                return;
            }

            await gerarPDFsEBaixar(lista);
        } catch (err) {
            console.error('Erro ao gerar ZIP:', err);
            alert('Erro ao gerar os PDFs. Tente novamente.');
        } finally {
            setGerando(false);
            setTipoGeracao(null);
        }
    };

    const handleGerarExcel = async () => {
        setGerando(true);
        setTipoGeracao('excel');
        try {
            const lista = await obterListaSelecionada();

            if (!lista || lista.length === 0) {
                alert("Nenhum registro selecionado.");
                return;
            }

            setProgresso({ total: lista.length, atual: 0 });
            const planilha = await montarDadosExcel(lista);
            setProgresso({ total: lista.length, atual: lista.length });

            await downloadXlsx([
                {
                    name: 'Formulários de Campo',
                    headers: planilha.headers,
                    rows: planilha.rows,
                },
            ], `formularios-campo-${planilha.periodoArquivo}.xlsx`);
        } catch (err) {
            console.error('Erro ao gerar Excel:', err);
            alert('Erro ao gerar a planilha. Tente novamente.');
        } finally {
            setGerando(false);
            setTipoGeracao(null);
            setProgresso({ total: 0, atual: 0 });
        }
    };

    const qtdSelecionada = modoTodos ? serverTotal : selecionados.size;

    const [localFilters, setLocalFilters] = useState({
        etiqueta: '',
        local: '',
        agente: '',
        atividade: '',
        data_atividade: '',
        data_exame: '',
        status: 'todos',
    });

    useEffect(() => {
        if (serverFilters) setLocalFilters(serverFilters);
    }, [serverFilters]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (onFilterChange) {
                const mudou = JSON.stringify(localFilters) !== JSON.stringify(serverFilters);
                if (mudou) onFilterChange(localFilters);
            }
        }, 600);
        return () => clearTimeout(timer);
    }, [localFilters, onFilterChange, serverFilters]);

    const formatarAtividade = (texto) => {
        if (!texto) return '-';
        const mapa = {
            'pesquisa': 'Pesquisa',
            'borrifacao': 'Borrifação',
            'borrifação': 'Borrifação',
            'atendimentopit': 'Atendimento ao PIT',
            'pit': 'PIT',
        };
        const chave = String(texto).toLowerCase().trim();
        return mapa[chave] || (texto.charAt(0).toUpperCase() + texto.slice(1));
    };

    const atividadesOpcoes = [
        { label: 'Pesquisa', value: 'pesquisa' },
        { label: 'Borrifação', value: 'borrifacao' },
        { label: 'Atendimento PIT', value: 'atendimentoPIT' },
    ];

    const handleClearFilters = () => {
        const empty = { etiqueta: '', local: '', agente: '', atividade: '', data_atividade: '', data_exame: '', status: 'todos' };
        setLocalFilters(empty);
        if (onFilterChange) onFilterChange(empty);
    };

    const hasFilters = localFilters.etiqueta || localFilters.local || localFilters.agente ||
        localFilters.atividade || localFilters.data_atividade || localFilters.status !== 'todos';

    const itemsPerPage = 50;
    const totalPages = Math.ceil(serverTotal / itemsPerPage);
    const startRecord = (serverPage - 1) * itemsPerPage + 1;
    const endRecord = Math.min(serverPage * itemsPerPage, serverTotal);

    return (
        <div className="workstation-container">
            <div className="workspace-panel">

                {/* ── Filtros ── */}
                <div className="toolbar-container">
                    <div className="filter-grid">
                        <div className="filter-item">
                            <label>Etiqueta</label>
                            <input
                                type="text"
                                placeholder="Etiqueta..."
                                value={localFilters.etiqueta}
                                onChange={e => setLocalFilters(p => ({ ...p, etiqueta: e.target.value }))}
                                className="filter-input input-small"
                            />
                        </div>
                        <div className="filter-item">
                            <label>Localidade</label>
                            <input
                                type="text"
                                placeholder="Localidade / Endereço..."
                                value={localFilters.local}
                                onChange={e => setLocalFilters(p => ({ ...p, local: e.target.value }))}
                                className="filter-input input-medium"
                            />
                        </div>
                        <div className="filter-item">
                            <label>Agente</label>
                            <select
                                value={localFilters.agente}
                                onChange={e => setLocalFilters(p => ({ ...p, agente: e.target.value }))}
                                className="filter-select"
                            >
                                <option value="">Todos os Agentes</option>
                                {allAgentsList.map(a => <option key={a.id} value={a.nome || a.nome_completo}>{a.nome || a.nome_completo}</option>)}
                            </select>
                        </div>
                        <div className="filter-item">
                            <label>Atividade</label>
                            <select
                                value={localFilters.atividade}
                                onChange={e => setLocalFilters(p => ({ ...p, atividade: e.target.value }))}
                                className="filter-select"
                            >
                                <option value="">Todas as Atividades</option>
                                {atividadesOpcoes.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                            </select>
                        </div>
                        <div className="filter-item">
                            <label htmlFor="wk_data_at">Data da Atividade</label>
                            <input
                                id="wk_data_at"
                                type="date"
                                value={localFilters.data_atividade}
                                onChange={e => setLocalFilters(p => ({ ...p, data_atividade: e.target.value }))}
                                className="filter-input"
                            />
                        </div>
                        <div className="filter-item">
                            <label>Status</label>
                            <select
                                value={localFilters.status}
                                onChange={e => setLocalFilters(p => ({ ...p, status: e.target.value }))}
                                className="filter-select"
                            >
                                <option value="todos">Todos</option>
                                <option value="pendente_campo">Pendente</option>
                                <option value="sincronizado">Sincronizado</option>
                            </select>
                        </div>
                        {hasFilters && (
                            <button className="btn-clear" onClick={handleClearFilters}>Limpar</button>
                        )}
                    </div>

                    <button
                        onClick={toggleModoSelecao}
                        className="btn-download-trigger"
                        style={{
                            background: modoSelecao ? '#1d4ed8' : '#2563eb',
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        {modoSelecao ? 'Cancelar seleção' : 'Exportar'}
                    </button>

                </div>

                {/* ── Barra de seleção ── */}
                {modoSelecao && (
                    <div className="selection-info-bar">
                        {qtdSelecionada === 0 ? (
                            <span className="selection-msg">
                                Selecione os formulários que deseja exportar
                            </span>
                        ) : (
                            <>
                                <span className="selection-status-text">
                                    {modoTodos
                                        ? `Todos os ${serverTotal} formulários selecionados`
                                        : `${selecionados.size} formulário${selecionados.size > 1 ? 's' : ''} desta página selecionado${selecionados.size > 1 ? 's' : ''}`}
                                </span>

                                {/* Oferecer selecionar todos do servidor */}
                                {todaPaginaSelecionada && !modoTodos && serverTotal > registros.length && (
                                    <button
                                        onClick={() => setModoTodos(true)}
                                        className="btn-select-all-server"
                                    >
                                        Selecionar todos os {serverTotal} registros
                                    </button>
                                )}

                                {modoTodos && (
                                    <button
                                        onClick={() => { setModoTodos(false); setSelecionados(new Set(registros.map(r => r.id))); }}
                                        className="btn-select-only-page"
                                    >
                                        Selecionar apenas esta página
                                    </button>
                                )}

                                <button
                                    onClick={handleBaixarZip}
                                    disabled={gerando}
                                    className="btn-execute-download"
                                    style={{
                                        background: gerando ? '#6b7280' : '#2563eb',
                                        cursor: gerando ? 'wait' : 'pointer',
                                    }}
                                >
                                    {gerando && tipoGeracao === 'pdf'
                                        ? (progresso.total > 0 ? `Gerando (${progresso.atual}/${progresso.total})...` : 'Iniciando...')
                                        : `Baixar ${qtdSelecionada} PDF${qtdSelecionada > 1 ? 's' : ''}`}
                                </button>

                                <button
                                    onClick={handleGerarExcel}
                                    disabled={gerando}
                                    className="btn-execute-download btn-execute-excel"
                                    style={{
                                        background: gerando ? '#6b7280' : '#059669',
                                        cursor: gerando ? 'wait' : 'pointer',
                                    }}
                                >
                                    {gerando && tipoGeracao === 'excel'
                                        ? (progresso.total > 0 && progresso.atual === progresso.total ? 'Baixando Excel...' : 'Analisando formulários...')
                                        : `Gerar Excel (${qtdSelecionada})`}
                                </button>
                            </>
                        )}
                    </div>
                )}

                <div className="pagination-info-top">
                    Exibindo registros <strong>{serverTotal > 0 ? startRecord : 0} – {endRecord}</strong> de <strong>{serverTotal}</strong>
                </div>

                {/* ── Tabela ── */}
                <div className="table-wrapper">
                    <table className="technical-grid">
                        <thead>
                            <tr>
                                {modoSelecao && (
                                    <th className="th-center" style={{ width: 36 }}>
                                        <input
                                            type="checkbox"
                                            checked={selecionados.size === registros.length && registros.length > 0}
                                            onChange={toggleTodos}
                                            title="Selecionar todos"
                                        />
                                    </th>
                                )}
                                <th className="th-center" style={{ width: 90 }}>Etiqueta</th>
                                <th className="th-center" style={{ width: 100 }}>Data</th>
                                <th className="th-left">Localidade / Endereço</th>
                                <th className="th-left" style={{ minWidth: 100 }}>Agente</th>
                                <th className="th-left" style={{ minWidth: 120 }}>Atividade</th>
                                <th className="th-center" style={{ minWidth: 120 }}>Captura</th>
                                <th className="th-center" style={{ minWidth: 150 }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {registros.map(row => {
                                const capturaCount = (row.barbeiros_intradomicilio || 0) + (row.barbeiros_peridomicilio || 0);
                                return (
                                    <tr
                                        key={row.id}
                                        style={modoSelecao && selecionados.has(row.id) ? { background: '#f0f9ff' } : {}}
                                    >
                                        {modoSelecao && (
                                            <td className="td-center" onClick={() => toggleSelecionado(row.id)} style={{ cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selecionados.has(row.id)}
                                                    onChange={() => toggleSelecionado(row.id)}
                                                    onClick={e => e.stopPropagation()}
                                                />
                                            </td>
                                        )}
                                        <td className="td-center code-font">
                                            {row.codigo_etiqueta || <span className="text-muted">-</span>}
                                        </td>
                                        <td className="td-center">
                                            {new Date(row.data_atividade.replace(/-/g, '/')).toLocaleDateString()}
                                        </td>
                                        <td className="td-left">
                                            <div className="fw-bold text-dark">{row.localidade || 'Local n/ inf.'}</div>
                                            <div className="text-small text-muted">{row.endereco}</div>
                                        </td>
                                        <td className="td-left text-small text-dark">{row.agente_nome}</td>
                                        <td className="td-left text-small text-dark" style={{ whiteSpace: 'nowrap' }}>
                                            {Array.isArray(row.tipo_atividade)
                                                ? row.tipo_atividade.map(a => formatarAtividade(a)).join(' / ')
                                                : formatarAtividade(row.tipo_atividade)}
                                        </td>
                                        <td className="td-center">
                                            {capturaCount > 0 ? (
                                                <span className="badge-capture" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>Captura {capturaCount}</span>
                                            ) : row.vestigios_encontrados ? (
                                                <span className="badge-trace" style={{ whiteSpace: 'nowrap' }}>Vestígios</span>
                                            ) : <span className="text-muted">-</span>}
                                        </td>
                                        <td className="td-center">
                                            <div className="action-cell">
                                                {row.sincronizado ? (
                                                    <>
                                                        <span className="status-text green" style={{ whiteSpace: 'nowrap' }}>Sincronizado</span>
                                                        <button
                                                            className="btn-primary-ghost"
                                                            onClick={() => navigate('/ficha-campo', { state: { data: row, mode: 'view' } })}
                                                        >
                                                            Visualizar
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="status-text orange" style={{ whiteSpace: 'nowrap' }}>Pendente</span>
                                                        <button
                                                            className="btn-outline"
                                                            onClick={() => navigate('/ficha-campo', { state: { data: row, mode: 'process' } })}
                                                        >
                                                            Processar
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {registros.length === 0 && <div className="empty-state">Nenhum registro encontrado.</div>}

                    {serverTotal > 0 && (
                        <div className="pagination-footer">
                            <button className="page-btn" disabled={serverPage === 1} onClick={() => onPageChange(serverPage - 1)}>
                                Anterior
                            </button>
                            <div className="page-numbers">
                                <button className="page-number active">{serverPage}</button>
                            </div>
                            <button className="page-btn" disabled={serverPage >= totalPages} onClick={() => onPageChange(serverPage + 1)}>
                                Próximo
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WorkstationTab;
